// src/services/customerService.js
import { supabase } from '../lib/supabase';
import { withTimeout } from '../lib/supabaseClient';
import { apiFetch } from './apiClient';

export const customerService = {
  // ============================================================================
  // CUSTOMER PREFERENCES
  // ============================================================================

  /**
   * Get customer preferences
   */
  async getCustomerPreferences(userId) {
    try {
      const fetchPromise = supabase
        .from('customer_preferences')
        .select('*')
        .eq('user_id', userId);

      const { data, error } = await withTimeout(fetchPromise, 3000);

      if (error) throw error;

      // Return first item if array, or null if empty
      return { data: data?.[0] || null, error: null };
    } catch (error) {
      console.error('Error fetching customer preferences:', error);
      return { data: null, error };
    }
  },

  /**
   * Create or update customer preferences
   */
  async updateCustomerPreferences(userId, preferences) {
    try {
      const { data, error } = await supabase
        .from('customer_preferences')
        .upsert({
          user_id: userId,
          ...preferences,
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating customer preferences:', error);
      return { data: null, error };
    }
  },

  // ============================================================================
  // BUSINESS CONNECTIONS (OPT-IN/OPT-OUT)
  // ============================================================================

  /**
   * Get all businesses the customer has opted into
   */
  async getOptedInBusinesses(userId) {
    try {
      // OR pattern — see PR #168 / #170 / #171. Two-embed approach
      // mirrors PR #170: pull both sides, resolve counterparty per-row in JS,
      // collapse back to a single `business` field for backwards compat with
      // CustomerDashboard's existing `conn.business?.business_profile` reads.
      const { data, error } = await supabase
        .from('business_customer_connections')
        .select(`
          id,
          business_id,
          customer_id,
          status,
          opted_in_at,
          business:business_id (
            id,
            email,
            full_name,
            business_profile:business_profiles (
              business_name,
              service_type,
              location,
              description
            )
          ),
          customer:customer_id (
            id,
            email,
            full_name,
            business_profile:business_profiles (
              business_name,
              service_type,
              location,
              description
            )
          )
        `)
        .or(`business_id.eq.${userId},customer_id.eq.${userId}`)
        .eq('status', 'active')
        .order('opted_in_at', { ascending: false });

      if (error) throw error;

      const normalised = (data || []).map(row => ({
        id: row.id,
        status: row.status,
        opted_in_at: row.opted_in_at,
        business: row.business_id === userId ? row.customer : row.business,
      }));
      return { data: normalised, error: null };
    } catch (error) {
      console.error('Error fetching opted-in businesses:', error);
      return { data: null, error };
    }
  },

  /**
   * Disconnect from a business. Goes through the API: clients have no write
   * access to business_customer_connections. Connections are created only
   * through invitations (the business invites, the customer accepts).
   */
  async disconnectFromBusiness(connectionId) {
    try {
      await apiFetch(`/v1/network/connections/${connectionId}`, { method: 'DELETE' });
      return { error: null };
    } catch (error) {
      console.error('Error disconnecting from business:', error);
      return { error };
    }
  },

  // ============================================================================
  // APPOINTMENT OFFERS — REMOVED in Wave 4
  // ============================================================================
  // The legacy offer-list and offer-response methods have been deleted.
  // Callers should use proposalService directly against the /v1/proposals API.

  // ============================================================================
  // AVAILABILITY
  // ============================================================================

  /**
   * Get customer's availability blocks
   */
  async getAvailability(userId, startDate = null, endDate = null) {
    try {
      let query = supabase
        .from('availability_blocks')
        .select('*')
        .eq('user_id', userId)
        .order('start_time');

      if (startDate) {
        query = query.gte('start_time', startDate.toISOString());
      }

      if (endDate) {
        query = query.lte('end_time', endDate.toISOString());
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching availability:', error);
      return { data: null, error };
    }
  },

  /**
   * Get customer's free time slots (no busy blocks)
   */
  async getFreeTimeSlots(userId, daysAhead = 14) {
    try {
      const startDate = new Date();
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + daysAhead);

      // Get all busy blocks
      const { data: busyBlocks, error } = await supabase
        .from('availability_blocks')
        .select('start_time, end_time')
        .eq('user_id', userId)
        .eq('is_available', false)
        .gte('start_time', startDate.toISOString())
        .lte('start_time', endDate.toISOString())
        .order('start_time');

      if (error) throw error;

      // Calculate free slots (simple version - returns busy times, UI can invert)
      return { data: busyBlocks, error: null };
    } catch (error) {
      console.error('Error fetching free time slots:', error);
      return { data: null, error };
    }
  },

  // ============================================================================
  // STATISTICS
  // ============================================================================

  /**
   * Get customer dashboard statistics
   */
  async getDashboardStats(userId, proposals = []) {
    // Wave 4: stats are derived from the proposals list the dashboard already
    // fetched via proposalService.list(). Pass that list in to avoid a second
    // round-trip; the only Supabase query left here counts opted-in businesses.
    try {
      // OR pattern — see PR #168 / #170 / #171.
      const businessesResult = await supabase
        .from('business_customer_connections')
        .select('id', { count: 'exact', head: true })
        .or(`business_id.eq.${userId},customer_id.eq.${userId}`)
        .eq('status', 'active');

      const now = Date.now();
      const pendingProposals = proposals.filter(p => p.status === 'proposed').length;
      const upcoming = proposals.filter(p => {
        if (p.status !== 'accepted') return false;
        const slot = (p.slots || []).find(s => s.id === p.selected_slot_id) || (p.slots || [])[0];
        if (!slot?.start_time) return false;
        return new Date(slot.start_time).getTime() >= now;
      }).length;

      return {
        data: {
          pendingOffers: pendingProposals,
          optedInBusinesses: businessesResult.count || 0,
          upcomingAppointments: upcoming
        },
        error: null
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      return { data: null, error };
    }
  }
};
